/**
 * S-08 «Разбор метрики» — из чего сложилось число на «Сводке» и кто его подвинул.
 * S-38 «Поиск» — товары, кампании и отзывы по названию, артикулу или номеру.
 */
import { useEffect, useRef, useState } from 'react'
import { fetchMetric, fetchSearch, type SearchResult } from '../api/sections.js'
import { Icon } from '../components/icons/Icon.js'
import { BarList, Spark } from '../components/charts/Charts.js'
import { Failed, Loading, NoShop, Truncation, useData, useScreenHead } from './common.js'
import { Num, Sect, ToneTag } from './ui.js'
import type { ScreenId } from '../router/screens.js'

type Open = (id: ScreenId, param?: string) => void

export function ScreenMetric({ code, open }: { code: string; open: Open }): React.JSX.Element {
  const { s, reload } = useData(() => fetchMetric(code))
  const m = s.kind === 'ok' && s.data.shop !== null ? s.data : null
  useScreenHead(m?.title, m?.period_display != null ? `за ${m.period_display}` : null)
  if (s.kind === 'loading') return <Loading label="Загрузка разбора" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (m === null) return <NoShop reason={s.data.reason} />
  return (
    <div data-testid="screen-body">
      {m.series !== undefined && <>
        <Sect title="По дням" aside={m.series.period_display} />
        <section className="card card-pad">
          <Spark points={m.series.points} label={m.series.summary} size="lg" />
          {m.series.note !== null && <p className="row-meta gap-top-s">{m.series.note}</p>}
        </section>
      </>}
      {m.explain !== undefined && <p className="hint">{m.explain}</p>}

      {m.bars !== undefined && <>
        <Sect title="По неделям" aside="закрытые отчёты WB" />
        <section className="card card-pad"><BarList rows={m.bars} label={m.bars_summary ?? ''} layout="stack" /></section>
      </>}

      {m.drivers !== undefined && m.drivers.length > 0 && <>
        <Sect title="Что подвинуло выплату" aside="к прошлой неделе" />
        <section className="card" data-testid="metric-drivers">
          {m.drivers.map((x, k) => (
            <div className="row" key={k}>
              <span className="row-main">
                <span className="row-title">{x.name.charAt(0).toUpperCase() + x.name.slice(1)}</span>
                <span className="row-meta">было <Num v={x.prev} /> → стало <Num v={x.now} /></span>
              </span>
              <span className="row-val"><ToneTag tone={x.tone} word={x.contribution.display ?? '—'} /></span>
            </div>
          ))}
          {m.drivers_note !== undefined && <p className="card-foot">{m.drivers_note}</p>}
        </section>
      </>}

      {m.contributors !== undefined && <>
        <Sect title={m.contributors.title} aside={m.contributors.rule} />
        <section className="card" data-testid="metric-contributors">
          {m.contributors.rows.map((r) => (
            <button type="button" className="row row-tap" key={r.nm_id} data-testid={'metric-sku-' + r.nm_id} onClick={() => open('S-12', String(r.nm_id))}>
              <span className="row-main"><span className="row-title">{r.title}</span></span>
              <span className="row-val">
                <span className="num">{r.now ?? '—'}</span>
                {r.delta !== null && <span className={'num-sub' + (r.tone === 'risk' ? ' is-down' : '')}>{r.delta}</span>}
              </span>
              <Icon name="nav-next" className="ic-chev row-end" />
            </button>
          ))}
        </section>
        <Truncation note={m.contributors.truncation} />
      </>}

      {m.campaigns !== undefined && <>
        <Sect title="Кампании" aside="по ДРР" />
        <section className="card">
          {m.campaigns.map((c) => (
            <button type="button" className="row row-tap" key={c.advert_id} onClick={() => open('S-14', String(c.advert_id))}>
              <span className="row-main"><span className="row-title">{c.name}</span><span className="row-meta">расход <Num v={c.spend} /></span></span>
              <span className="row-val"><span className="num"><Num v={c.drr} /></span></span>
              <Icon name="nav-next" className="ic-chev row-end" />
            </button>
          ))}
        </section>
      </>}
    </div>
  )
}

export function ScreenSearch({ open }: { open: Open }): React.JSX.Element {
  useScreenHead(undefined, null)
  const [q, setQ] = useState('')
  const [res, setRes] = useState<{ q: string; data: SearchResult | null; fail: string | null } | null>(null)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { input.current?.focus() }, [])
  // Debounce 300 мс (ТЗ S-38): запрос уходит, когда человек перестал печатать.
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setRes(null); return }
    let alive = true
    const t = setTimeout(() => {
      void fetchSearch(term).then((r) => { if (alive) setRes({ q: term, data: r.ok ? r.data : null, fail: r.ok ? null : r.error.message }) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [q])
  const go = (kind: string, id: string): void => {
    if (kind === 'products') open('S-12', id)
    else if (kind === 'campaigns') open('S-14', id)
    else open('S-19', id)
  }
  return (
    <div data-testid="screen-body">
      <form className="search-box" role="search" onSubmit={(e) => { e.preventDefault(); input.current?.blur() }}>
        <Icon name="section-search" className="ic-muted" />
        <label className="vh" htmlFor="search-q">Поиск</label>
        <input id="search-q" ref={input} className="search-inp" type="search" inputMode="search" enterKeyHint="search"
          autoComplete="off" value={q} data-testid="search-q" onChange={(e) => setQ(e.target.value)} placeholder="Название, артикул или номер" />
        {q !== '' && (
          <button type="button" className="search-clear" aria-label="Очистить" onClick={() => { setQ(''); input.current?.focus() }}>
            <Icon name="action-decline" size={20} />
          </button>
        )}
      </form>
      {q.trim().length < 2 && <p className="hint">Введите название товара, артикул продавца, артикул WB, номер кампании или слова из отзыва.</p>}
      {res !== null && res.fail !== null && <Failed message={res.fail} />}
      {res !== null && res.data !== null && res.data.groups.length === 0 && (
        <p className="hint" data-testid="search-empty">Ничего не найдено по запросу «{res.q}».</p>
      )}
      {res?.data?.groups.map((g) => (
        <section key={g.kind}>
          <Sect title={g.title} aside={`${g.items.length}${g.items.length >= 20 ? '+' : ''}`} />
          <div className="card">
            {g.items.map((it) => (
              <button type="button" className="row row-tap" key={g.kind + it.id} data-testid={`search-${g.kind}-${it.id}`} onClick={() => go(g.kind, it.id)}>
                <span className="row-main"><span className="row-title">{it.title}</span><span className="row-meta">{it.sub}</span></span>
                <Icon name="nav-next" className="ic-chev row-end" />
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
